/**
 * Whether the console exists in this environment at all.
 *
 * `VITE_CONSOLE_ENABLED` wins in both directions when it is set. When it is unset — which is every
 * environment today, because the dashboard ships no `.env` file and no workflow supplies this key —
 * the environment decides: on locally and on dev, off on stage and prod.
 *
 * That default is what makes the scaffold visible with zero configuration while keeping it out of
 * production, and it is why no `.github/workflows` change ships with ORB-756.
 *
 * ⚑ **This is resolved at build time, not at run time.** Vite substitutes `import.meta.env.VITE_*`
 * into the bundle when `npm run build` runs, so the value is fixed the moment the SPA is built.
 * Setting an app setting on the Web App does nothing, and fails silently: the built bundle no longer
 * contains a lookup to satisfy (ORB-770).
 *
 * To turn the console on in a deployed environment, add `VITE_CONSOLE_ENABLED` to the `env:` block
 * feeding the build step in that environment's SPA workflow, then redeploy. The three are named for
 * the **branch** they deploy from, not the environment, so only dev's name matches:
 *
 * - dev — `.github/workflows/dev_orbitalx-development.yml`
 * - stage — `.github/workflows/stage_orbitalx-stage.yml`
 * - prod — `.github/workflows/main_orbitalx-prod.yml`
 */

import { getEnvironment } from '@/constants/env'

export function isConsoleEnabled(): boolean {
  const flag = (import.meta.env.VITE_CONSOLE_ENABLED as string | undefined)?.trim().toLowerCase()
  if (flag === 'true') return true
  if (flag === 'false') return false

  const environment = getEnvironment()
  return environment === 'local' || environment === 'dev'
}
